/**
 * Settings View
 * Configuration interface for system settings and calibration
 */

import { useState } from 'react'
import { useMScaleStore } from '../store'
import { useBluetooth } from '../hooks/useBluetooth'
import { GridView } from '../components/Grid/GridView'
import { GroupPanel } from '../components/Group/GroupPanel'
import { Card } from '../components/Common/Card'
import { Button } from '../components/Common/Button'
import { CalibrationView } from './CalibrationView'
import { FirmwareUpload } from '../components/OTA/FirmwareUpload'
import { DualDeviceControls } from '../components/DualDevice'

export function SettingsView() {
  const { tareAll, setLEDWhite, updateTargetRange, updateGroupSize, updateMinRatio, readCustomSuffix, writeCustomSuffix } = useBluetooth()
  const { targetRange, groupSize, minRatio, lastWeightUpdate, hardwareConfig, isDualDeviceMode, deviceName, showHundredthsUnder10g, setShowHundredthsUnder10g } = useMScaleStore()
  const [showCalibration, setShowCalibration] = useState(false)

  const scaleCount = isDualDeviceMode ? 32 : (hardwareConfig?.scaleCount ?? 16)

  // Local state for editing settings
  const [editingSettings, setEditingSettings] = useState(false)
  const [minWeight, setMinWeight] = useState(parseFloat(targetRange.min.toFixed(2)))
  const [maxWeight, setMaxWeight] = useState(parseFloat(targetRange.max.toFixed(2)))
  const [selectedGroupSize, setSelectedGroupSize] = useState(groupSize)
  const [selectedMinRatio, setSelectedMinRatio] = useState(parseFloat(minRatio.toFixed(2)))

  // Local state for device name editing
  const [editingDeviceName, setEditingDeviceName] = useState(false)
  const [customSuffix, setCustomSuffix] = useState('')
  const [suffixError, setSuffixError] = useState('')

  const handleTareAll = async () => {
    if (confirm('Tare all scales? This will zero all scale readings.')) {
      try {
        await tareAll()
      } catch (error) {
        alert('Failed to tare scales')
      }
    }
  }

  const handleSaveSettings = async () => {
    try {
      await updateTargetRange(minWeight, maxWeight)
      await updateGroupSize(selectedGroupSize)
      await updateMinRatio(selectedMinRatio)
      setEditingSettings(false)
    } catch (error) {
      alert('Failed to save settings')
    }
  }

  const handleCancelEdit = () => {
    setMinWeight(parseFloat(targetRange.min.toFixed(2)))
    setMaxWeight(parseFloat(targetRange.max.toFixed(2)))
    setSelectedGroupSize(groupSize)
    setSelectedMinRatio(parseFloat(minRatio.toFixed(2)))
    setEditingSettings(false)
  }

  const getGroupSizeLabel = (size: number) => {
    switch (size) {
      case 2: return 'Pairs'
      case 3: return 'Triplets'
      case 4: return 'Quads'
      default: return `Groups of ${size}`
    }
  }

  const handleEditDeviceName = async () => {
    try {
      const currentSuffix = await readCustomSuffix()
      setCustomSuffix(currentSuffix || '')
      setEditingDeviceName(true)
      setSuffixError('')
    } catch (error) {
      alert('Failed to read current device name suffix')
    }
  }

  const handleSaveDeviceName = async () => {
    // Validate suffix
    const upperSuffix = customSuffix.toUpperCase().trim()
    if (upperSuffix.length !== 4) {
      setSuffixError('Suffix must be exactly 4 characters')
      return
    }
    if (!/^[0-9A-F]{4}$/.test(upperSuffix)) {
      setSuffixError('Suffix must contain only hex characters (0-9, A-F)')
      return
    }

    try {
      await writeCustomSuffix(upperSuffix)
      setEditingDeviceName(false)
      setSuffixError('')
      alert('Device name suffix saved! Restart the device to see the new name.')
    } catch (error) {
      setSuffixError(error instanceof Error ? error.message : 'Failed to save')
    }
  }

  const handleCancelDeviceNameEdit = () => {
    setEditingDeviceName(false)
    setCustomSuffix('')
    setSuffixError('')
  }

  if (showCalibration) {
    return <CalibrationView onBack={() => setShowCalibration(false)} />
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
      {/* Device Name Card */}
      <Card title="Device Name" className="mb-4 sm:mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Current Device Name
            </label>
            <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {deviceName || 'M-Scale'}
            </div>
          </div>

          {editingDeviceName ? (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Suffix (Last 4 Digits)
              </label>
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-2">
                Enter 4 hex characters (0-9, A-F).
              </p>
              <input
                type="text"
                value={customSuffix}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase()
                  if (value.length <= 4) {
                    setCustomSuffix(value)
                    setSuffixError('')
                  }
                }}
                placeholder="A1B2"
                maxLength={4}
                className={`w-full px-3 py-2 min-h-[44px] text-sm sm:text-base border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  suffixError
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {suffixError && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {suffixError}
                </p>
              )}
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
                Example: M-Scale-{customSuffix || 'XXXX'}
              </p>
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-[10px] sm:text-xs text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> The new name will take effect after you restart the device.
                  Due to Bluetooth caching, you may need to "forget" this device in your system's
                  Bluetooth settings before the new name appears in the connection list.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Customize the last 4 digits of your M-Scale's device name for easy identification.
            </p>
          )}

          <div className="flex gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {editingDeviceName ? (
              <>
                <Button variant="primary" onClick={handleSaveDeviceName} className="flex-1">
                  Save Name
                </Button>
                <Button variant="secondary" onClick={handleCancelDeviceNameEdit} className="flex-1">
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={handleEditDeviceName} className="w-full sm:w-auto">
                Edit Device Name
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Settings Card */}
      <Card title="Algorithm Settings" className="mb-4 sm:mb-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Group Size */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Group Size
            </label>
            {editingSettings ? (
              <div className="flex gap-2 flex-wrap">
                {[2, 3, 4].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedGroupSize(size)}
                    className={`flex-1 min-w-[80px] px-3 sm:px-4 py-2 min-h-[44px] rounded-lg font-medium transition-colors text-sm sm:text-base ${
                      selectedGroupSize === size
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {getGroupSizeLabel(size)}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {getGroupSizeLabel(groupSize)} ({groupSize} items per group)
              </div>
            )}
          </div>

          {/* Target Weight Range */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Weight Range
            </label>
            {editingSettings ? (
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Min (g)
                  </label>
                  <input
                    type="number"
                    value={minWeight}
                    onChange={(e) => setMinWeight(parseFloat(e.target.value))}
                    step="0.1"
                    className="w-full px-3 py-2 min-h-[44px] text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <span className="text-gray-500 dark:text-gray-400 mt-5 sm:mt-6">-</span>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Max (g)
                  </label>
                  <input
                    type="number"
                    value={maxWeight}
                    onChange={(e) => setMaxWeight(parseFloat(e.target.value))}
                    step="0.1"
                    className="w-full px-3 py-2 min-h-[44px] text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            ) : (
              <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {targetRange.min.toFixed(1)}g - {targetRange.max.toFixed(1)}g
              </div>
            )}
          </div>

          {/* Minimum Ratio */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minimum Cell Weight Ratio
            </label>
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-2">
              Minimum ratio between smallest and largest cell in a group (0 = disabled)
            </p>
            {editingSettings ? (
              <div>
                <input
                  type="number"
                  value={selectedMinRatio}
                  onChange={(e) => setSelectedMinRatio(parseFloat(e.target.value))}
                  min="0"
                  max="1"
                  step="0.05"
                  className="w-full px-3 py-2 min-h-[44px] text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Range: 0.0 (no limit) to 1.0 (perfectly matched)
                </p>
              </div>
            ) : (
              <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {minRatio === 0 ? 'Disabled' : minRatio.toFixed(2)}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap">
            {editingSettings ? (
              <>
                <Button variant="primary" onClick={handleSaveSettings} className="flex-1 min-w-[120px]">
                  Save Settings
                </Button>
                <Button variant="secondary" onClick={handleCancelEdit} className="flex-1 min-w-[120px]">
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => setEditingSettings(true)} className="w-full sm:w-auto">
                Edit Settings
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Display Settings Card */}
      <Card title="Display Settings" className="mb-4 sm:mb-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                Show Hundredths Under 10g
              </label>
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                Display two decimal places for weights under 10 grams (e.g., 5.23g instead of 5.2g)
              </p>
            </div>
            <button
              onClick={() => setShowHundredthsUnder10g(!showHundredthsUnder10g)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                showHundredthsUnder10g ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={showHundredthsUnder10g}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  showHundredthsUnder10g ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 italic">
            This setting is saved in your browser.
          </p>
        </div>
      </Card>

      {/* Controls */}
      <div className="mb-4 sm:mb-6 flex items-center justify-between flex-wrap gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
          {lastWeightUpdate && (
            <div className="text-[10px] sm:text-xs text-gray-500 dark:text-industrial-text-dim">
              Last Update: {new Date(lastWeightUpdate).toLocaleTimeString()}
            </div>
          )}
        </div>
        <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
          <Button variant="secondary" onClick={handleTareAll} className="flex-1 sm:flex-none">
            Tare All
          </Button>
          <Button variant="primary" onClick={() => setShowCalibration(true)} className="flex-1 sm:flex-none">
            Calibrate
          </Button>
        </div>
      </div>

      {/* Main Grid and Pairing Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Grid View - Takes up 2 columns on large screens */}
        <div className="lg:col-span-2">
          <Card title="Scale Grid">
            <GridView onHover={(position) => setLEDWhite(position, 500)} />

            {hardwareConfig && (
              <div className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500 dark:text-industrial-text-dim text-center">
                {scaleCount} scales {isDualDeviceMode ? '(dual-device mode)' : '(single device)'} •
                {' '}{isDualDeviceMode ? '32 active' : `${hardwareConfig.activePositions.length} active`}
              </div>
            )}
          </Card>
        </div>

        {/* Pairing Panel - 1 column */}
        <div className="lg:col-span-1">
          <GroupPanel />
        </div>
      </div>

      {/* Dual-Device Controls */}
      <div className="mb-4 sm:mb-6">
        <DualDeviceControls />
      </div>

      {/* Firmware Update */}
      <FirmwareUpload />

      {/* System Info */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="p-3 sm:p-4 bg-white dark:bg-industrial-surface rounded-lg shadow">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-industrial-text-dim mb-1">
            Update Rate
          </div>
          <div className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            500ms
          </div>
        </div>
        <div className="p-3 sm:p-4 bg-white dark:bg-industrial-surface rounded-lg shadow">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-industrial-text-dim mb-1">
            Grid Size
          </div>
          <div className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {hardwareConfig?.gridRows ?? 4} × {hardwareConfig?.gridCols ?? 4}
          </div>
        </div>
        <div className="p-3 sm:p-4 bg-white dark:bg-industrial-surface rounded-lg shadow">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-industrial-text-dim mb-1">
            Connection
          </div>
          <div className="text-lg sm:text-xl font-semibold text-green-600 dark:text-green-400">
            Active
          </div>
        </div>
      </div>

      {/* Quick Help */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-xs sm:text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Color Codes
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{ backgroundColor: '#00FF00' }} />
            <span className="text-blue-800 dark:text-blue-300">Green - {groupSize === 2 ? 'Pair' : 'Group'} 1</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{ backgroundColor: '#0000FF' }} />
            <span className="text-blue-800 dark:text-blue-300">Blue - {groupSize === 2 ? 'Pair' : 'Group'} 2</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{ backgroundColor: '#FFFF00' }} />
            <span className="text-blue-800 dark:text-blue-300">Yellow - {groupSize === 2 ? 'Pair' : 'Group'} 3</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{ backgroundColor: '#FF0000' }} />
            <span className="text-blue-800 dark:text-blue-300">Red - {groupSize === 2 ? 'Pair' : 'Group'} 4</span>
          </div>
        </div>
      </div>
    </div>
  )
}
